/**
 * Data block: reads a named property value from a SceneObject's Properties behavior.
 * Resolves via the ancestor tree walk — same semantics as $name in behavior data.
 * Pure data node — no flow sockets. Output is pulled when a downstream
 * execution node needs the value.
 */

import { FlowGraphBlock } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeAny } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { resolveTarget } from './traceContext';
import { Logger } from '../../../utils/Logger';

export class TraceGetPropertyBlock extends FlowGraphBlock {
    private _targetPath: string;
    private _propertyName: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; property?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._propertyName = config?.property ?? '';
        this.registerDataOutput("value", RichTypeAny);
    }

    public override _updateOutputs(context: FlowGraphContext): void {
        const target = resolveTarget(context, this._targetPath);
        const resolved = target?.resolveProperty(this._propertyName);
        if (resolved == null) {
            Logger.warn(`[TraceGetPropertyBlock] Property "${this._propertyName}" not resolved on "${target?.name ?? this._targetPath}"`);
        }

        // vec3 property values are plain [x, y, z] arrays; convert to Vector3
        // so they survive polymorphic math blocks, which dispatch on
        // getClassName() — raw arrays fall through to the scalar branch and
        // NaN. TraceSetPropertyBlock converts instances back to arrays at the
        // write seam, so get → math/* → set round-trips.
        //
        // Deliberately NOT converted:
        // - vec4: the engine's float4 policy (Quaternion) doesn't apply —
        //   Vector4Declaration is component-wise generic data (RGBA, weights)
        //   and quaternion-multiply semantics would be wrong for it.
        // - color: hex strings, not arrays — nothing to convert.
        let value: unknown = resolved;
        if (Array.isArray(resolved) && resolved.length === 3) {
            value = Vector3.FromArray(resolved);
        }

        this.getDataOutput("value")!.setValue(value ?? undefined, context);
    }

    public override getClassName(): string {
        return "TraceGetPropertyBlock";
    }
}
